// Equipe — a vista única do hub RH (2026-08-25).
//
// Uma tabela pessoa-cêntrica no lugar das duas abas antigas (Profissionais e
// Documentos): cada linha é uma pessoa, com cargo, admissão, pendências de
// documento, saldo de férias e cobertura de horas do mês; o clique abre a
// FichaProfissional (?pessoa=<uuid> na URL, para o link ser compartilhável).
//
// SEIS fontes, cada uma com try/catch próprio — 403 faz o BLOCO sumir, nunca
// a tela inteira:
//  1. GET /profissionais?incluir_inativos=1 (dre-engine; array cru) — base da
//     SecaoCadastro; salário vem PODADO sem perm (ausência da chave, não 403);
//  2. action rh_equipe (comercial-engine) — cargo/admissão/férias por pessoa;
//  3. action rh_resumo — placar de documentos + pendências + vencendo;
//  4. GET /financeiro/horas/cobertura (mês corrente) — 403 sem
//     horas.ver_equipe faz a coluna Cobertura sumir;
//  5. action profissional_clockify_listar + clockify_listar_user_map;
//  6. GET /admin/usuarios — sugestões do vínculo de login na SecaoCadastro.
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert, Button, Card, Empty, Flex, Input, Progress, Space, Switch, Table,
  Tag, Tooltip, Typography, App as AntApp,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, RightOutlined, SearchOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr } from '../../utils/dataFmt';
import { formatarHoras } from '../operacional-horas/horasUtils';
import { PendenciaRH, RhResumo, pedirRhResumo, fmtPendencia } from './docsRH';
import type { ClockifyCfg, Profissional } from './SecaoCadastro';
import { usePerms } from '../../hooks/usePerms';
import ModalNovaPessoa from '../../components/pessoas/ModalNovaPessoa';
import AutoPreencherClockify from './AutoPreencherClockify';
import NovoProfissional from './NovoProfissional';
import FichaProfissional, {
  RecursosFicha, CoberturaPessoa, acaoRH,
} from './FichaProfissional';

const { Text, Title } = Typography;

// ── Tipos da action rh_equipe (handlers/rh.js › rhEquipe) ──────────────────
type FeriasSaldo = {
  direito: number | string;
  gozados: number | string;
  saldo: number | string;
};

type PessoaEquipe = {
  id: string;                       // uuid de public.profissionais
  nome: string;
  ativo: boolean;
  usuario_email: string | null;
  cargo: string | null;
  admissao: string | null;
  desligamento: string | null;
  regime: string | null;
  ferias: FeriasSaldo | null;       // null = sem data de admissão
};

type LinhaEquipe = PessoaEquipe & {
  pendencias: PendenciaRH[];
  cobertura?: CoberturaPessoa;
};

const fmtDias = (v: number | string | null | undefined): string => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
};

const corCobertura = (pct: number | null) =>
  pct === null ? '#999' : pct >= 90 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f';

export default function EquipeRH(): ReactElement {
  const { message } = AntApp.useApp();
  const [sp, setSp] = useSearchParams();
  const pessoaParam = sp.get('pessoa');

  const [profissionais, setProfissionais] = useState<Profissional[]>([]);
  const [equipe, setEquipe] = useState<PessoaEquipe[]>([]);
  const [semAcessoEquipe, setSemAcessoEquipe] = useState(false);
  const [resumo, setResumo] = useState<RhResumo | null>(null);
  // null = sem acesso (ou erro) — a coluna Cobertura some.
  const [cobertura, setCobertura] = useState<CoberturaPessoa[] | null>(null);
  const [clockifyCfgMap, setClockifyCfgMap] = useState<Record<string, ClockifyCfg>>({});
  const [clockifyUsers, setClockifyUsers] = useState<any[]>([]);
  const [usuariosSistema, setUsuariosSistema] = useState<{ email: string; nome: string }[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [busca, setBusca] = useState('');
  const [mostrarDesligados, setMostrarDesligados] = useState(false);
  const [autoAberto, setAutoAberto] = useState(false);
  // Duas portas de entrada (28/09/2026):
  //  · "Nova pessoa" — login + ficha + organograma de uma vez (ciclo da pessoa,
  //    só o administrador do sistema);
  //  · "Novo profissional (sem login)" — ficha de quem não entra no sistema
  //    (freelancer, autônomo), pelo RH (administrativo.rh.documentos.gerenciar);
  //    `pode_gerenciar` vem do rh_equipe. O login dela, se um dia houver, é
  //    pelo "Vincular login" da ficha.
  const [podeGerenciar, setPodeGerenciar] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);
  const { pode } = usePerms();
  const podeCiclo = pode('admin.permissoes.editar');
  const [novaPessoa, setNovaPessoa] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    // Cada fonte falha sozinha: 403 numa não pode derrubar as outras.
    await Promise.all([
      (async () => {
        try {
          const token = await getToken();
          const r = await axios.get(`${API_URL}/profissionais?incluir_inativos=1`,
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          setProfissionais(Array.isArray(d) ? d : []);
        } catch { setProfissionais([]); }
      })(),
      (async () => {
        try {
          const d = await acaoRH<{ pessoas: PessoaEquipe[]; pode_gerenciar: boolean }>('rh_equipe');
          setEquipe(d.pessoas || []);
          setPodeGerenciar(d.pode_gerenciar === true);
          setSemAcessoEquipe(false);
        } catch (e: any) {
          setEquipe([]);
          setPodeGerenciar(false);
          setSemAcessoEquipe(e?.response?.status === 403);
        }
      })(),
      (async () => {
        try { setResumo(await pedirRhResumo()); }
        catch { setResumo(null); }   // 403: placar e pendências somem
      })(),
      (async () => {
        try {
          const token = await getToken();
          const r = await axios.get(
            `${API_URL}/financeiro/horas/cobertura?competencia=${dayjs().format('YYYY/MM')}`,
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          setCobertura(d.pessoas || []);
        } catch { setCobertura(null); }   // 403 sem horas.ver_equipe: coluna some
      })(),
      (async () => {
        try {
          const d = await acaoRH<{ profissionais: any[] }>('profissional_clockify_listar');
          const map: Record<string, ClockifyCfg> = {};
          for (const p of (d?.profissionais || [])) {
            map[p.id] = {
              salario_esperado: p.salario_esperado !== null ? Number(p.salario_esperado) : null,
              coeficiente: p.coeficiente !== null ? Number(p.coeficiente) : 1.0,
              usa_clockify_rateio: !!p.usa_clockify_rateio,
              clockify_user_id: p.clockify_user_id || null,
              clockify_user_email: p.clockify_user_email || null,
              clockify_user_name: p.clockify_user_name || null,
            };
          }
          setClockifyCfgMap(map);
        } catch { setClockifyCfgMap({}); }
      })(),
      (async () => {
        try {
          const d = await acaoRH<any>('clockify_listar_user_map');
          setClockifyUsers(Array.isArray(d) ? d : []);
        } catch { setClockifyUsers([]); }
      })(),
      (async () => {
        try {
          const token = await getToken();
          const r = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          setUsuariosSistema((Array.isArray(d) ? d : [])
            .filter((u: any) => u.email)
            .map((u: any) => ({ email: String(u.email), nome: u.nome || '' }))
            .sort((a: any, b: any) => (a.nome || a.email).localeCompare(b.nome || b.email)));
        } catch { setUsuariosSistema([]); }
      })(),
    ]);
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Sinal de permissão financeira: a chave salario_esperado só vem na resposta
  // de quem tem financeiro.profissionais.ver (mesma régua da tela antiga).
  const temPermFin = useMemo(
    () => profissionais.some((p) => 'salario_esperado' in p),
    [profissionais]);

  const pendenciasPorPessoa = useMemo(() => {
    const m = new Map<string, PendenciaRH[]>();
    for (const p of (resumo?.pessoas ?? [])) m.set(String(p.id), p.pendencias || []);
    return m;
  }, [resumo]);

  const coberturaPorPessoa = useMemo(() => {
    const m = new Map<string, CoberturaPessoa>();
    for (const c of (cobertura ?? [])) m.set(String(c.profissional_id), c);
    return m;
  }, [cobertura]);

  const linhas = useMemo<LinhaEquipe[]>(() => {
    // rh_equipe é a base; se ela falhou (403), o GET /profissionais segura a
    // lista com o mínimo (nome + ativo) para a tela não ficar vazia.
    const base: PessoaEquipe[] = equipe.length > 0
      ? equipe
      : profissionais.map((p) => ({
        id: String(p.id), nome: p.nome_canonical, ativo: p.ativo,
        usuario_email: p.usuario_email ?? null,
        cargo: null, admissao: null, desligamento: null, regime: null, ferias: null,
      }));
    const q = busca.trim().toLowerCase();
    return base
      .filter((p) => mostrarDesligados || p.ativo)
      .filter((p) => !q || p.nome.toLowerCase().includes(q))
      .map((p) => ({
        ...p,
        pendencias: pendenciasPorPessoa.get(String(p.id)) || [],
        cobertura: coberturaPorPessoa.get(String(p.id)),
      }));
  }, [equipe, profissionais, busca, mostrarDesligados, pendenciasPorPessoa, coberturaPorPessoa]);

  // ── Placar (mesma régua do antigo PainelDocumentosRH) ────────────────────
  // `dias` e `docs` podem chegar como string do Postgres — Number() antes de
  // qualquer comparação. Os cards seguem a chave "Desligados".
  const mapaResumo = useMemo(() => {
    const m = new Map<string, { ativo: boolean }>();
    for (const p of (resumo?.pessoas ?? [])) m.set(String(p.id), { ativo: p.ativo });
    return m;
  }, [resumo]);
  const contaPessoa = useCallback((pid: string | null | undefined): boolean => {
    if (mostrarDesligados) return true;
    const p = pid ? mapaResumo.get(String(pid)) : undefined;
    return !p || p.ativo;   // documento sem pessoa continua contando
  }, [mostrarDesligados, mapaResumo]);
  const pendentes = useMemo(
    () => (resumo?.pessoas ?? []).filter((p) => p.ativo && (p.pendencias || []).length > 0),
    [resumo]);
  const vencendo60 = useMemo(
    () => (resumo?.vencendo ?? []).filter((v) => Number(v.dias) >= 0 && contaPessoa(v.profissional_id)),
    [resumo, contaPessoa]);
  const vencidos = useMemo(
    () => (resumo?.vencendo ?? []).filter((v) => Number(v.dias) < 0 && contaPessoa(v.profissional_id)),
    [resumo, contaPessoa]);
  const comDocs = useMemo(
    () => (resumo?.pessoas ?? []).filter((p) => Number(p.docs) > 0 && (p.ativo || mostrarDesligados)),
    [resumo, mostrarDesligados]);

  // ── Navegação para a ficha (?pessoa= na URL) ─────────────────────────────
  const abrirPessoa = useCallback((id: string) => {
    const next = new URLSearchParams(sp);
    next.set('pessoa', id);
    setSp(next);
  }, [sp, setSp]);

  const voltarDaFicha = useCallback(() => {
    const next = new URLSearchParams(sp);
    next.delete('pessoa');
    setSp(next);
  }, [sp, setSp]);

  if (pessoaParam) {
    const recursos: RecursosFicha = {
      profissionais, clockifyCfgMap, clockifyUsers, usuariosSistema,
    };
    return (
      <FichaProfissional
        profissionalId={pessoaParam}
        aoVoltar={voltarDaFicha}
        recursos={recursos}
        onRecarregar={carregar}
      />
    );
  }

  // ── Colunas (só as que a permissão sustenta) ─────────────────────────────
  const temDadosRH = equipe.length > 0;
  const cols: ColumnsType<LinhaEquipe> = [
    {
      // SEM width: coluna elástica — fica com a sobra da tela.
      title: 'Nome', dataIndex: 'nome', key: 'nome',
      render: (v: string, r) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v}>
          <Text strong>{v}</Text>
          {!r.ativo && <Tag style={{ fontSize: 9, marginLeft: 6 }}>desligado</Tag>}
        </div>
      ),
    },
  ];
  if (temDadosRH) {
    cols.push(
      {
        title: 'Cargo', dataIndex: 'cargo', key: 'cargo', width: 150,
        render: (v: string | null) => v
          ? <span style={{ fontSize: 12 }}>{v}</span>
          : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
      },
      {
        title: 'Admissão', dataIndex: 'admissao', key: 'admissao', width: 96,
        render: (v: string | null) => (
          <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDataBr(v)}</span>
        ),
      },
    );
  }
  if (resumo) {
    cols.push({
      title: 'Pendências', key: 'pendencias', width: 230,
      render: (_, r) => {
        if (!r.ativo) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        if (!r.pendencias.length) return <Tag color="green">em dia</Tag>;
        return (
          <Space size={4} wrap>
            {r.pendencias.map((p, i) => (
              <Tag key={i} color={p.situacao === 'vencendo' ? 'orange' : 'red'}
                style={{ marginInlineEnd: 0 }}>
                {fmtPendencia(p)}
              </Tag>
            ))}
          </Space>
        );
      },
    });
  }
  if (temDadosRH) {
    cols.push({
      title: 'Saldo férias', key: 'ferias', width: 100, align: 'right',
      render: (_, r) => {
        if (!r.ferias) {
          return (
            <Tooltip title="Sem data de admissão — preencha na ficha para calcular">
              <Text type="secondary">—</Text>
            </Tooltip>
          );
        }
        const saldo = Number(r.ferias.saldo);
        return (
          <Tooltip title={`Direito ${fmtDias(r.ferias.direito)} d, gozados/agendados ${fmtDias(r.ferias.gozados)} d`}>
            <Text strong style={{ color: saldo < 0 ? '#B91626' : undefined }}>
              {fmtDias(saldo)} d
            </Text>
          </Tooltip>
        );
      },
    });
  }
  if (cobertura !== null) {
    cols.push({
      title: 'Cobertura do mês', key: 'cobertura', width: 160,
      render: (_, r) => {
        const c = r.cobertura;
        if (!c) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        return (
          <Tooltip title={`${formatarHoras(c.minutos)} apontadas de ${formatarHoras(c.minutos_esperados)} esperadas`}>
            <Progress percent={Math.min(c.cobertura_pct ?? 0, 100)} size="small"
              strokeColor={corCobertura(c.cobertura_pct)}
              format={() => (c.cobertura_pct === null ? '—' : `${c.cobertura_pct}%`)} />
          </Tooltip>
        );
      },
    });
  }
  cols.push({
    title: '', key: 'abrir', width: 88, align: 'center',
    render: (_, r) => (
      <Button size="small" onClick={(e) => { e.stopPropagation(); abrirPessoa(String(r.id)); }}>
        Abrir <RightOutlined style={{ fontSize: 10 }} />
      </Button>
    ),
  });

  // Piso da tabela: soma das colunas visíveis + espaço decente para o Nome.
  const larguraMinima = 200 /* Nome mínimo */ + 88
    + (temDadosRH ? 150 + 96 + 100 : 0)
    + (resumo ? 230 : 0)
    + (cobertura !== null ? 160 : 0);

  const cards: { key: string; label: string; n: number; ajuda: string }[] = resumo
    ? [
      { key: 'pendencias', label: 'Pendências', n: pendentes.length,
        ajuda: 'Pessoas ativas com documento obrigatório faltando, vencendo ou vencido' },
      { key: 'vencendo', label: 'Vencendo em 60 dias', n: vencendo60.length,
        ajuda: 'Documentos com validade nos próximos 60 dias' },
      { key: 'vencidos', label: 'Vencidos', n: vencidos.length,
        ajuda: 'Documentos com a validade já passada' },
      { key: 'pessoas', label: 'Pessoas com documentos', n: comDocs.length,
        ajuda: 'Pessoas com ao menos um documento no acervo' },
    ]
    : [];

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="flex-start" wrap gap={12} style={{ marginBottom: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Equipe</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Cada linha é uma pessoa — clique para abrir a ficha completa
            (documentos, férias, horas, remuneração e rateio).
          </Text>
        </div>
        <Space wrap>
          <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>Atualizar</Button>
          {podeCiclo && (
            <Button type="primary" size="small" onClick={() => setNovaPessoa(true)}>
              ＋ Nova pessoa
            </Button>
          )}
          {podeGerenciar && (
            <Button size="small" icon={<PlusOutlined />}
              onClick={() => setNovoAberto(true)}>
              Novo profissional (sem login)
            </Button>
          )}
          {temPermFin && (
            <Button size="small" icon={<ThunderboltOutlined />}
              onClick={() => setAutoAberto(true)}>
              Auto-preencher (Clockify)
            </Button>
          )}
        </Space>
      </Flex>

      {novaPessoa && (
        <ModalNovaPessoa aberto onClose={() => setNovaPessoa(false)} onSalvo={() => carregar()} />
      )}

      {cards.length > 0 && (
        <Flex gap={8} wrap style={{ marginBottom: 12 }}>
          {cards.map((c) => (
            <Tooltip key={c.key} title={c.ajuda}>
              <Card size="small"
                style={{ minWidth: 130, opacity: c.n === 0 ? 0.55 : 1 }}
                styles={{ body: { padding: '8px 12px' } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{c.label}</Text>
                <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.1 }}>{c.n}</div>
              </Card>
            </Tooltip>
          ))}
        </Flex>
      )}

      {/* O que FALTA não tem linha em tabela nenhuma — só existe aqui. */}
      {pendentes.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={pendentes.length === 1
            ? '1 pessoa com documento obrigatório pendente'
            : `${pendentes.length} pessoas com documento obrigatório pendente`}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {pendentes.map((p) => (
                <li key={p.id} style={{ fontSize: 12 }}>
                  <strong>{p.nome}</strong>: {p.pendencias.map(fmtPendencia).join(', ')}
                </li>
              ))}
            </ul>
          } />
      )}

      {semAcessoEquipe && profissionais.length > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Sem acesso aos dados de RH (cargo, admissão e férias) — mostrando só o cadastro de profissionais." />
      )}

      <Flex gap={8} align="center" wrap style={{ marginBottom: 12 }}>
        <Input value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome..."
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          size="small" style={{ flex: 1, minWidth: 200, maxWidth: 360 }} allowClear />
        <Tooltip title="Mostrar também quem já saiu da empresa">
          <Space size={4}>
            <Switch size="small" checked={mostrarDesligados} onChange={setMostrarDesligados} />
            <Text style={{ fontSize: 12 }}>Desligados</Text>
          </Space>
        </Tooltip>
      </Flex>

      <Table<LinhaEquipe>
        rowKey="id" columns={cols} dataSource={linhas} loading={carregando}
        // Sem `fixed`, largura declarada é sugestão; com x numérico o antd
        // trata como MIN-width e a tabela estica quando sobra tela.
        tableLayout="fixed" scroll={{ x: larguraMinima }}
        size="small" pagination={{ pageSize: 50 }}
        onRow={(r) => ({
          onClick: () => abrirPessoa(String(r.id)),
          style: { cursor: 'pointer' },
        })}
        locale={{
          emptyText: <Empty description={carregando
            ? ' '
            : 'Nenhuma pessoa encontrada. Os profissionais são criados pela importação financeira; a ficha de RH se preenche aqui.'} />,
        }}
      />

      <AutoPreencherClockify
        aberto={autoAberto}
        onFechar={() => setAutoAberto(false)}
        aoAplicar={carregar} />

      {novoAberto && (
        <NovoProfissional
          usuariosSistema={usuariosSistema}
          onFechar={() => setNovoAberto(false)}
          aoCriar={(id, aviso) => {
            setNovoAberto(false);
            // Recarrega a lista e vai direto para a ficha: cadastrar é meio
            // caminho — CPF, documentos e férias entram lá, e a pessoa já está
            // com o contexto na cabeça.
            carregar();
            abrirPessoa(id);
            if (aviso) message.info({ content: aviso, duration: 8 });
          }} />
      )}
    </div>
  );
}
